import { io, Socket } from 'socket.io-client';
import { GameEvent } from '~/modules/GameEvents/GameEvents';
import type { ClientToServerEvents, ServerToClientEvents } from './messages';

const SOCKET_URL = import.meta.env.VITE_APP_MULTIPLAYER_URL || '';
const SYNC_SAMPLES = 5;

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export const connectionStateChanged = new GameEvent<(state: ConnectionState) => void>(
  'mp:connectionStateChanged',
  false,
  false,
);

class SocketClient {
  private socket: TypedSocket | null = null;
  private clockOffset = 0;
  private syncSamples: number[] = [];
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private syncPongHandler: ((data: { clientSendTime: number; serverTime: number }) => void) | null = null;
  private _connectionState: ConnectionState = 'disconnected';
  private _previousId: string | null = null;
  private _onReconnect: (() => void) | null = null;

  get id(): string | null {
    return this.socket?.id ?? null;
  }

  get previousId(): string | null {
    return this._previousId;
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  get connectionState(): ConnectionState {
    return this._connectionState;
  }

  set onReconnect(handler: (() => void) | null) {
    this._onReconnect = handler;
  }

  private setConnectionState(state: ConnectionState): void {
    this._connectionState = state;
    connectionStateChanged.dispatch(state);
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      this.setConnectionState('connecting');

      this.socket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: 15,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      }) as TypedSocket;

      let initialConnect = true;

      this.socket.on('connect', () => {
        if (initialConnect) {
          initialConnect = false;
          this.setConnectionState('connected');
          this.startClockSync();
          resolve();
        } else {
          // This is a reconnect - socket got a new ID
          this.setConnectionState('connected');
          this.startClockSync();
          this._onReconnect?.();
        }
      });

      this.socket.on('connect_error', (err) => {
        if (initialConnect) {
          initialConnect = false;
          this.setConnectionState('disconnected');
          reject(err);
        }
      });

      this.socket.on('disconnect', () => {
        // Save the ID before disconnect so we can rejoin with it
        if (this.socket?.id) {
          this._previousId = this.socket.id;
        }
        this.setConnectionState('reconnecting');
      });

      this.socket.io.on('reconnect_failed', () => {
        this.setConnectionState('disconnected');
      });
    });
  }

  disconnect(): void {
    this.stopClockSync();
    this._onReconnect = null;
    this._previousId = null;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.clockOffset = 0;
    this.syncSamples = [];
    this.setConnectionState('disconnected');
  }

  emit<E extends keyof ClientToServerEvents>(event: E, ...args: Parameters<ClientToServerEvents[E]>): void {
    this.socket?.emit(event, ...args);
  }

  on(event: string, callback: (...args: any[]) => void): void {
    this.socket?.on(event as any, callback as any);
  }

  off(event: string, callback?: (...args: any[]) => void): void {
    if (callback) {
      this.socket?.off(event as any, callback as any);
    } else {
      this.socket?.off(event as any);
    }
  }

  /**
   * Get server-aligned timestamp (Date.now() + offset to match server clock)
   */
  getServerTime(): number {
    return Date.now() + this.clockOffset;
  }

  /**
   * Calculate delay until a server timestamp occurs (in ms)
   */
  getDelayUntil(serverTimestamp: number): number {
    return serverTimestamp - this.getServerTime();
  }

  private stopClockSync(): void {
    if (this.syncPongHandler && this.socket) {
      this.socket.off('sync:pong', this.syncPongHandler as any);
    }
    this.syncPongHandler = null;
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  private startClockSync(): void {
    this.stopClockSync();
    this.syncSamples = [];

    const handler = (data: { clientSendTime: number; serverTime: number }) => {
      const clientReceiveTime = Date.now();
      const rtt = clientReceiveTime - data.clientSendTime;
      const offset = data.serverTime - data.clientSendTime - rtt / 2;

      this.syncSamples.push(offset);
      while (this.syncSamples.length > SYNC_SAMPLES * 2) {
        this.syncSamples.shift();
      }

      const sorted = [...this.syncSamples].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      this.clockOffset = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    };

    this.syncPongHandler = handler;
    this.socket?.on('sync:pong', handler as any);

    const sendPing = () => {
      this.socket?.emit('sync:ping', { clientSendTime: Date.now() });
    };

    for (let i = 0; i < SYNC_SAMPLES; i++) {
      setTimeout(sendPing, i * 200);
    }

    this.syncInterval = setInterval(() => {
      if (!this.socket?.connected) {
        this.stopClockSync();
        return;
      }
      sendPing();
    }, 30_000);
  }
}

export default new SocketClient();
